import { useAppTranslation } from '../i18n/useAppTranslation';
import MessageComposer from "../components/MessageComposer";
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useConfirm } from "../components/ConfirmDialog";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  TextField,
  Button,
  Box,
  Divider,
  ListItemButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Email,
  Circle,
  ArrowBack,
  Close,
  Add,
  Dashboard as DashboardIcon,
  Group as GroupIcon,
  EventNote,
  Assessment,
  PersonAdd,
  Chat,
  Payment as PaymentIcon2,
  ManageAccounts,
  Search as SearchIcon,
  CalendarMonth,
  TrendingUp,
  Receipt as ReceiptIcon,
  Menu as MenuIcon,
  DeleteOutline,
  Delete as DeleteIcon,
  Groups,
  PersonRemove,
} from "@mui/icons-material";
import { Tabs, Tab } from "@mui/material";
import api from "../services/api";
import { format } from "date-fns";

import Sidebar, { SidebarItem } from "../components/Sidebar";

interface User {
  id: number;
  username: string;
  role: string;
  email?: string;
}

interface Message {
  id: number;
  sender: number;
  receiver: number;
  content: string;
  timestamp: string;
  is_read: boolean;
  sender_details: User;
  receiver_details: User;
}

interface Conversation {
  user: User;
  lastMessage: Message;
  messages: Message[];
  unreadCount: number;
}

interface GroupMessage {
  id: number;
  group: number;
  sender: number;
  sender_details: User;
  content: string;
  timestamp: string;
}

interface Group {
  id: number;
  name: string;
  creator: number;
  creator_details: User;
  members: number[];
  members_details: User[];
  last_message: { content: string; sender: string; timestamp: string } | null;
}

const Messaging = () => {
  const { tr, dateLocale } = useAppTranslation();
  const { user, logout } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [hoveredMsgId, setHoveredMsgId] = useState<number | null>(null);
  const [confirmDeleteConvo, setConfirmDeleteConvo] = useState<User | null>(
    null
  );
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);

  // Contacts disponibles pour nouvelle conversation
  const [availableContacts, setAvailableContacts] = useState<User[]>([]);
  const [openNewConvoModal, setOpenNewConvoModal] = useState(false);
  const [contactSearch, setContactSearch] = useState("");

  // Invitation State
  const [openInviteModal, setOpenInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({
    email: "",
    name: "",
    custom_message: "",
  });
  const [inviteStatus, setInviteStatus] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Groups State
  const [panelTab, setPanelTab] = useState<0 | 1>(0); // 0 = Conversations, 1 = Groupes
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [groupMessages, setGroupMessages] = useState<GroupMessage[]>([]);
  const [openCreateGroupModal, setOpenCreateGroupModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupMemberIds, setNewGroupMemberIds] = useState<number[]>([]);
  const [groupMemberSearch, setGroupMemberSearch] = useState("");
  const [openManageGroupModal, setOpenManageGroupModal] = useState(false);
  const groupBottomRef = useRef<HTMLDivElement | null>(null);
  // Unread group messages tracking: { [groupId]: count }
  const [unreadGroupMap, setUnreadGroupMap] = useState<{
    [groupId: number]: number;
  }>({});
  const selectedGroupRef = useRef<Group | null>(null);

  const totalUnreadGroups = Object.values(unreadGroupMap).reduce(
    (a, b) => a + b,
    0
  );

  const handleDeleteMessage = useCallback(async (msgId: number) => {
    try {
      await api.delete(`/messaging/messages/${msgId}/`);
      setMessages((prev) => prev.filter((m) => m.id !== msgId));
      setConversations((prev) =>
        prev
          .map((c) => {
            const remaining = c.messages.filter((m) => m.id !== msgId);
            if (remaining.length === 0) return null as any;
            return {
              ...c,
              messages: remaining,
              lastMessage: remaining[remaining.length - 1],
            };
          })
          .filter(Boolean)
      );
    } catch (e) {
      console.error("Error deleting message", e);
    }
  }, []);

  const handleDeleteConversation = useCallback(
    async (otherUser: User) => {
      try {
        await api.delete(
          `/messaging/messages/delete_conversation/?with=${otherUser.id}`
        );
        setConversations((prev) =>
          prev.filter((c) => c.user.id !== otherUser.id)
        );
        if (selectedUser?.id === otherUser.id) {
          setSelectedUser(null);
          setMessages([]);
        }
        setConfirmDeleteConvo(null);
      } catch (e) {
        console.error("Error deleting conversation", e);
      }
    },
    [selectedUser]
  );

  const bottomRef = useRef<HTMLDivElement | null>(null);
  const selectedUserRef = useRef<User | null>(null);

  const fetchGroups = useCallback(async () => {
    try {
      const res = await api.get("/messaging/groups/");
      setGroups(res.data);
    } catch (e) {
      console.error("fetchGroups error", e);
    }
  }, []);

  const fetchGroupMessages = useCallback(async (groupId: number) => {
    try {
      const res = await api.get(
        `/messaging/group-messages/?group_id=${groupId}`
      );
      setGroupMessages(res.data);
    } catch (e) {
      console.error("fetchGroupMessages error", e);
    }
  }, []);

  const handleSelectGroup = async (group: Group) => {
    setSelectedGroup(group);
    setSelectedUser(null);
    await fetchGroupMessages(group.id);
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      const res = await api.post("/messaging/groups/", {
        name: newGroupName.trim(),
        member_ids: newGroupMemberIds,
      });
      setGroups((prev) => [res.data, ...prev]);
      setOpenCreateGroupModal(false);
      setNewGroupName("");
      setNewGroupMemberIds([]);
      setGroupMemberSearch("");
    } catch (e: any) {
      console.error("handleCreateGroup error", e?.response?.data || e);
    }
  };

  const handleSendGroupMessage = async (content: string) => {
    if (!selectedGroup) throw new Error("No group selected");
    try {
      const res = await api.post("/messaging/group-messages/", {
        group: selectedGroup.id,
        content,
      });
      if (selectedGroupRef.current?.id === selectedGroup.id) setGroupMessages((prev) => {
        if (prev.find((m) => m.id === res.data.id)) return prev;
        return [...prev, res.data];
      });
      setGroups((prev) =>
        prev.map((g) =>
          g.id === selectedGroup.id
            ? {
                ...g,
                last_message: {
                  content,
                  sender: user?.username || "",
                  timestamp: new Date().toISOString(),
                },
              }
            : g
        )
      );
    } catch (e) {
      console.error("handleSendGroupMessage error", e);
      throw e;
    }
  };

  const handleDeleteGroup = async (groupId: number) => {
    const ok = await confirm({
      title: "Supprimer ce groupe ?",
      description:
        "Tous les messages du groupe seront définitivement supprimés pour l'ensemble des membres.",
      confirmLabel: "Supprimer le groupe",
    });
    if (!ok) return;
    try {
      await api.delete(`/messaging/groups/${groupId}/`);
      setGroups((prev) => prev.filter((g) => g.id !== groupId));
      if (selectedGroup?.id === groupId) {
        setSelectedGroup(null);
        setGroupMessages([]);
      }
    } catch (e) {
      console.error("handleDeleteGroup error", e);
    }
  };

  const handleRemoveGroupMember = async (groupId: number, userId: number) => {
    try {
      const res = await api.post(
        `/messaging/groups/${groupId}/remove_member/`,
        { user_id: userId }
      );
      setGroups((prev) => prev.map((g) => (g.id === groupId ? res.data : g)));
      if (selectedGroup?.id === groupId) setSelectedGroup(res.data);
    } catch (e: any) {
      toast.error(
        e?.response?.data?.detail || "Erreur lors de la suppression du membre."
      );
    }
  };

  const handleAddGroupMember = async (groupId: number, userId: number) => {
    try {
      const res = await api.post(`/messaging/groups/${groupId}/add_member/`, {
        user_id: userId,
      });
      setGroups((prev) => prev.map((g) => (g.id === groupId ? res.data : g)));
      if (selectedGroup?.id === groupId) setSelectedGroup(res.data);
    } catch (e: any) {
      toast.error(
        e?.response?.data?.detail || "Erreur lors de l'ajout du membre."
      );
    }
  };

  useEffect(() => {
    selectedUserRef.current = selectedUser;
  }, [selectedUser]);

  useEffect(() => {
    selectedGroupRef.current = selectedGroup;
    if (selectedGroup) {
      setUnreadGroupMap((prev) => ({ ...prev, [selectedGroup.id]: 0 }));
    }
  }, [selectedGroup]);

  // Determine effective role for navigation context
  const state = location.state as {
    recipient?: User;
    roleMode?: string;
  } | null;
  const effectiveRole = state?.roleMode || user?.role;

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        if (effectiveRole === "COACH" || effectiveRole === "ADMIN") {
          const [sportifsRes, coachesRes] = await Promise.all([
            api.get("/coaching/clients/"),
            api.get("/users/coaches/"),
          ]);
          // /users/coaches/ est pagine ({ results, count }) ; /coaching/clients/ ne l'est pas.
          const coachesList = Array.isArray(coachesRes.data)
            ? coachesRes.data
            : coachesRes.data.results ?? [];
          const sportifs = sportifsRes.data.map((s: any) => ({
            ...s,
            _group: "Mes Sportifs",
          }));
          const coaches = coachesList
            .filter((c: any) => c.id !== user?.id)
            .map((c: any) => ({ ...c, _group: "Coachs" }));
          setAvailableContacts([...sportifs, ...coaches]);
        } else {
          const res = await api.get("/users/coaches/");
          const coachesList = Array.isArray(res.data)
            ? res.data
            : res.data.results ?? [];
          setAvailableContacts(
            coachesList.map((c: any) => ({ ...c, _group: "Coachs" }))
          );
        }
      } catch (e) {
        console.error("Failed to fetch contacts", e);
      }
    };
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveRole]);

  const selectedConversationId = useMemo(
    () => selectedUser?.id ?? null,
    [selectedUser]
  );

  const fetchMessages = useCallback(async () => {
    try {
      const response = await api.get("/messaging/messages/");
      const allMessages: Message[] = response.data;

      // Group messages by other user
      const convos: { [key: number]: Conversation } = {};
      allMessages.forEach((msg) => {
        const other =
          Number(msg.sender) === Number(user?.id)
            ? msg.receiver_details
            : msg.sender_details;
        if (!other) return;

        if (!convos[other.id]) {
          convos[other.id] = {
            user: other,
            lastMessage: msg,
            messages: [],
            unreadCount: 0,
          };
        }
        convos[other.id].messages.push(msg);

        if (msg.receiver === user?.id && !msg.is_read) {
          convos[other.id].unreadCount++;
        }

        // Update last message if this one is newer
        if (
          new Date(msg.timestamp) >
          new Date(convos[other.id].lastMessage.timestamp)
        ) {
          convos[other.id].lastMessage = msg;
        }
      });

      const sortedConvos = Object.values(convos).sort(
        (a, b) =>
          new Date(b.lastMessage.timestamp).getTime() -
          new Date(a.lastMessage.timestamp).getTime()
      );
      setConversations(sortedConvos);
      return sortedConvos;
    } catch (error) {
      console.error("Failed to fetch messages", error);
      return [];
    }
  }, [user?.id]);

  // WebSocket Connection
  useEffect(() => {
    fetchMessages(); // Initial fetch
    fetchGroups();

    if (!user) return;

    const token = localStorage.getItem("access_token");
    // Use production WebSocket URL
    const wsUrl = `wss://g1sport.fr/ws/chat/?token=${token}`;
    const chatSocket = new WebSocket(wsUrl);

    chatSocket.onopen = () => {
      console.log("Connected to Chat WebSocket");
    };

    chatSocket.onmessage = (e) => {
      const message = JSON.parse(e.data);

      // Message de groupe
      if (message.is_group) {
        const groupId = message.group_id;
        // Ajouter au chat si le groupe est ouvert
        if (selectedGroupRef.current?.id === groupId) {
          setGroupMessages((prev) => {
            if (prev.find((m) => m.id === message.id)) return prev;
            return [...prev, message];
          });
          // Remettre le compteur à 0 puisqu'on est dans le groupe
          setUnreadGroupMap((prev) => ({ ...prev, [groupId]: 0 }));
        } else if (Number(message.sender) !== Number(user.id)) {
          // Incrémenter non-lus si on n'est pas l'expéditeur
          setUnreadGroupMap((prev) => ({
            ...prev,
            [groupId]: (prev[groupId] || 0) + 1,
          }));
          // Mettre à jour last_message du groupe dans la liste
          setGroups((prev) =>
            prev.map((g) =>
              g.id === groupId
                ? {
                    ...g,
                    last_message: {
                      content: message.content,
                      sender: message.sender_details?.username || "",
                      timestamp: message.timestamp,
                    },
                  }
                : g
            )
          );
        }
        return;
      }

      setConversations((prevConvos) => {
        const otherUserId =
          Number(message.sender) === Number(user.id)
            ? message.receiver
            : message.sender;
        const existingIndex = prevConvos.findIndex(
          (c) => c.user.id === otherUserId
        );

        let newConvos = [...prevConvos];
        let targetConvo: Conversation;

        if (existingIndex > -1) {
          targetConvo = { ...newConvos[existingIndex] };
          // Deduplicate
          if (!targetConvo.messages.find((m) => m.id === message.id)) {
            targetConvo.messages = [...targetConvo.messages, message];
            targetConvo.lastMessage = message;

            // Increment unread if I am receiver and it's not read
            // Only increment if we are not currently chatting with this user
            if (message.receiver === user.id && !message.is_read) {
              if (selectedUserRef.current?.id !== otherUserId) {
                targetConvo.unreadCount += 1;
              }
            }
          }
          // Move to top
          newConvos.splice(existingIndex, 1);
          newConvos.unshift(targetConvo);
        } else {
          // New conversation
          const otherUser =
            Number(message.sender) === Number(user.id)
              ? message.receiver_details
              : message.sender_details;
          if (otherUser) {
            targetConvo = {
              user: otherUser,
              lastMessage: message,
              messages: [message],
              unreadCount:
                message.receiver === user.id && !message.is_read ? 1 : 0,
            };
            newConvos.unshift(targetConvo);
          }
        }
        return newConvos;
      });

      // Update active messages view
      if (selectedUserRef.current) {
        const otherUserId =
          Number(message.sender) === Number(user.id)
            ? message.receiver
            : message.sender;
        if (selectedUserRef.current.id === otherUserId) {
          setMessages((prev) => {
            if (prev.find((m) => m.id === message.id)) return prev;
            return [...prev, message];
          });

          // Mark read if I'm the receiver and viewing it
          if (message.receiver === user.id) {
            api
              .post("/messaging/messages/mark_read/", {
                sender_id: otherUserId,
              })
              .catch(console.error);
          }
        }
      }
    };

    chatSocket.onclose = () => {
      console.log("Chat WebSocket disconnected");
    };

    return () => {
      chatSocket.close();
    };
  }, [user, fetchMessages]);

  useEffect(() => {
    const init = async () => {
      const loadedConvos = await fetchMessages();

      const state = location.state as { recipient?: User };
      if (state?.recipient) {
        const existingConvo = loadedConvos.find(
          (c) => c.user.id === state.recipient?.id
        );
        if (existingConvo) {
          handleSelectConversation(existingConvo);
        } else {
          setSelectedUser(state.recipient);
          setMessages([]);
        }
      }
    };
    init();
  }, [fetchMessages, location.state]);

  const handleSelectConversation = async (convo: Conversation) => {
    setSelectedUser(convo.user);
    const sorted = [...convo.messages].sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
    setMessages(sorted);

    if (convo.unreadCount > 0) {
      try {
        await api.post("/messaging/messages/mark_read/", {
          sender_id: convo.user.id,
        });
        // Update local unread count
        setConversations((prev) =>
          prev.map((c) =>
            c.user.id === convo.user.id ? { ...c, unreadCount: 0 } : c
          )
        );
      } catch (error) {
        console.error("Failed to mark messages as read", error);
      }
    }
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, selectedConversationId]);

  useEffect(() => {
    groupBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [groupMessages.length]);

  // Polling messages groupe actif toutes les 3s
  useEffect(() => {
    if (!selectedGroup) return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get(
          `/messaging/group-messages/?group_id=${selectedGroup.id}`
        );
        const fetched: GroupMessage[] = res.data;
        setGroupMessages((prev) => {
          if (fetched.length === prev.length) return prev;
          return fetched;
        });
      } catch (e) {
        /* silencieux */
      }
    }, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup?.id]);

  // Polling tous les groupes toutes les 5s pour détecter les nouveaux messages non lus
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await api.get("/messaging/groups/");
        const fetched: Group[] = res.data;
        setGroups((prev) => {
          // Détecter les nouveaux messages par rapport à l'état actuel
          fetched.forEach((newG) => {
            const oldG = prev.find((g) => g.id === newG.id);
            const isOpen = selectedGroupRef.current?.id === newG.id;
            if (
              !isOpen &&
              newG.last_message &&
              oldG?.last_message?.content !== newG.last_message.content
            ) {
              setUnreadGroupMap((m) => ({
                ...m,
                [newG.id]: (m[newG.id] || 0) + 1,
              }));
            }
          });
          return fetched;
        });
      } catch (e) {
        /* silencieux */
      }
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Polling toutes les 3s pour recevoir les messages en temps réel (fallback WebSocket local)
  useEffect(() => {
    if (!selectedUser) return;
    const interval = setInterval(async () => {
      try {
        const response = await api.get("/messaging/messages/");
        const allMessages: Message[] = response.data;
        const convos: { [key: number]: Conversation } = {};
        allMessages.forEach((msg) => {
          const other =
            Number(msg.sender) === Number(user?.id)
              ? msg.receiver_details
              : msg.sender_details;
          if (!other) return;
          if (!convos[other.id]) {
            convos[other.id] = {
              user: other,
              lastMessage: msg,
              messages: [],
              unreadCount: 0,
            };
          }
          convos[other.id].messages.push(msg);
          if (msg.receiver === user?.id && !msg.is_read)
            convos[other.id].unreadCount++;
          if (
            new Date(msg.timestamp) >
            new Date(convos[other.id].lastMessage.timestamp)
          ) {
            convos[other.id].lastMessage = msg;
          }
        });
        const sorted = Object.values(convos).sort(
          (a, b) =>
            new Date(b.lastMessage.timestamp).getTime() -
            new Date(a.lastMessage.timestamp).getTime()
        );
        setConversations(sorted);
        // Mettre à jour les messages de la conversation active
        const active = sorted.find(
          (c) => c.user.id === selectedUserRef.current?.id
        );
        if (active) {
          const sortedMsgs = [...active.messages].sort(
            (a, b) =>
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );
          setMessages(sortedMsgs);
        }
      } catch (e) {
        /* silencieux */
      }
    }, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser, user?.id]);

  const handleSendMessage = async (content: string) => {
    if (!selectedUser) throw new Error("No recipient selected");
    try {
      const res = await api.post("/messaging/messages/", {
        receiver: selectedUser.id,
        content,
      });
      const sent = res.data;
      // Affichage optimiste : ajouter le message immédiatement
      if (selectedUserRef.current?.id === selectedUser.id) setMessages((prev) => {
        if (prev.find((m) => m.id === sent.id)) return prev;
        return [...prev, sent];
      });
      // Mettre à jour la liste des conversations
      setConversations((prev) => {
        const idx = prev.findIndex((c) => c.user.id === selectedUser.id);
        if (idx > -1) {
          const updated = [...prev];
          updated[idx] = {
            ...updated[idx],
            lastMessage: sent,
            messages: updated[idx].messages.some((m) => m.id === sent.id)
              ? updated[idx].messages
              : [...updated[idx].messages, sent],
          };
          return updated;
        }
        return [
          {
            user: selectedUser,
            lastMessage: sent,
            messages: [sent],
            unreadCount: 0,
          },
          ...prev,
        ];
      });
    } catch (error) {
      console.error("Failed to send message", error);
      throw error;
    }
  };

  const handleSendInvite = async () => {
    setInviteStatus(null);
    try {
      await api.post("/coaching/invite/", { ...inviteData, mode: "email" });
      setInviteStatus({
        type: "success",
        text: "Invitation envoyée avec succès !",
      });
      setTimeout(() => {
        setOpenInviteModal(false);
        setInviteStatus(null);
        setInviteData({ email: "", name: "", custom_message: "" });
      }, 2000);
    } catch (error) {
      console.error("Error sending invitation", error);
      setInviteStatus({
        type: "error",
        text: "Erreur lors de l'envoi de l'invitation.",
      });
    }
  };

  const totalUnread =
    conversations.reduce((a, c) => a + c.unreadCount, 0) + totalUnreadGroups;

  const coachSidebarItems: SidebarItem[] = [
    {
      label: "Tableau de bord",
      icon: <DashboardIcon fontSize="small" />,
      index: 0,
      navigateTo: "/coach/dashboard",
      navigateState: { tab: 0 },
    },
    {
      label: "Sportifs",
      icon: <GroupIcon fontSize="small" />,
      index: 1,
      navigateTo: "/coach/dashboard",
      navigateState: { tab: 1 },
    },
    {
      label: "Séances",
      icon: <EventNote fontSize="small" />,
      index: 2,
      navigateTo: "/coach/dashboard",
      navigateState: { tab: 2 },
    },
    {
      label: "Performances",
      icon: <Assessment fontSize="small" />,
      index: 3,
      navigateTo: "/coach/dashboard",
      navigateState: { tab: 3 },
    },
    {
      label: "Invitations",
      icon: <PersonAdd fontSize="small" />,
      index: 4,
      navigateTo: "/coach/dashboard",
      navigateState: { tab: 4 },
    },
    {
      label: "Messages",
      icon: <Chat fontSize="small" />,
      index: 5,
      badge: totalUnread || undefined,
    },
    {
      label: "Paiements",
      icon: <PaymentIcon2 fontSize="small" />,
      index: 6,
      navigateTo: "/coach/dashboard",
      navigateState: { tab: 5 },
    },
    {
      label: "Mon Profil",
      icon: <ManageAccounts fontSize="small" />,
      index: 7,
      navigateTo: "/coach/dashboard",
      navigateState: { tab: 6 },
    },
  ];

  const sportifSidebarItems: SidebarItem[] = [
    {
      label: "Tableau de bord",
      icon: <DashboardIcon fontSize="small" />,
      index: 0,
      navigateTo: "/sportif/dashboard",
      navigateState: { tab: 0 },
    },
    {
      label: "Trouver un Coach",
      icon: <SearchIcon fontSize="small" />,
      index: 1,
      navigateTo: "/sportif/dashboard",
      navigateState: { tab: 1 },
    },
    {
      label: "Mes Réservations",
      icon: <CalendarMonth fontSize="small" />,
      index: 2,
      navigateTo: "/sportif/dashboard",
      navigateState: { tab: 2 },
    },
    {
      label: "Performances",
      icon: <TrendingUp fontSize="small" />,
      index: 3,
      navigateTo: "/sportif/dashboard",
      navigateState: { tab: 3 },
    },
    {
      label: "Messages",
      icon: <Chat fontSize="small" />,
      index: 4,
      badge: totalUnread || undefined,
    },
    {
      label: "Invitations",
      icon: <PersonAdd fontSize="small" />,
      index: 5,
      navigateTo: "/sportif/dashboard",
      navigateState: { tab: 5 },
    },
    {
      label: "Paiements",
      icon: <ReceiptIcon fontSize="small" />,
      index: 6,
      navigateTo: "/sportif/dashboard",
      navigateState: { tab: 6 },
    },
    {
      label: "Mon Profil",
      icon: <ManageAccounts fontSize="small" />,
      index: 7,
      navigateTo: "/sportif/dashboard",
      navigateState: { tab: 7 },
    },
  ];

  const sidebarItems =
    effectiveRole === "COACH" || effectiveRole === "ADMIN"
      ? coachSidebarItems
      : sportifSidebarItems;
  const activeIndex = sidebarItems.findIndex((i) => i.label === "Messages");

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        minHeight: "100vh",
        display: "flex",
      }}
    >
      <Sidebar
        items={sidebarItems}
        activeIndex={activeIndex}
        onSelect={() => {}}
        user={user}
        onLogout={logout}
        role={effectiveRole || "SPORTIF"}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />
      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isMobile && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              pb: 1.5,
              pt: "max(12px, env(safe-area-inset-top))",
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
              position: "sticky",
              top: 0,
              zIndex: 100,
            }}
          >
            <IconButton
              aria-label={tr("Ouvrir le menu")}
              size="small"
              onClick={() => setMobileSidebarOpen(true)}
            >
              <MenuIcon />
            </IconButton>
            <Box component="img" src="/g1s-logo-3d.png" sx={{ height: 32 }} />
            <Typography variant="h6" sx={{ fontWeight: 900, color: "#FFD700" }}>
              G1Sport
            </Typography>
          </Box>
        )}
        <Container
          maxWidth="xl"
          sx={{
            mt: isMobile ? 2 : 4,
            mb: 4,
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            px: isMobile ? 2 : 3,
          }}
        >
          {/* Header Section with Invite/Contact Button */}
          {(!isMobile || (!selectedUser && !selectedGroup)) && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography
                variant={isMobile ? "h5" : "h4"}
                fontWeight="800"
                color="text.primary"
              >
                 {tr("Messages")} </Typography>

              <Button
                variant="contained"
                color="primary"
                startIcon={<PersonAdd />}
                onClick={() => {
                  if (effectiveRole === "COACH" || effectiveRole === "ADMIN") {
                    navigate("/coach/dashboard", { state: { tab: 4 } });
                  } else {
                    navigate("/sportif/dashboard", { state: { tab: 5 } });
                  }
                }}
                sx={{
                  borderRadius: 3,
                  px: isMobile ? 2 : 3,
                  py: 1,
                  fontWeight: 700,
                  boxShadow: "0 8px 16px rgba(25, 118, 210, 0.3)",
                  minWidth: isMobile ? "auto" : "unset",
                }}
              >
                {isMobile ? tr("Invitations") : tr("Consulter les invitations")}
              </Button>
            </Box>
          )}

          <Grid
            container
            spacing={2}
            sx={{
              flexGrow: 1,
              maxHeight: isMobile
                ? "calc(100vh - 140px)"
                : "calc(100vh - 180px)",
            }}
          >
            {/* Conversations List */}
            <Grid
              size={{ xs: 12, md: 4, lg: 3 }}
              sx={{
                height: "100%",
                display:
                  isMobile && (selectedUser || selectedGroup)
                    ? "none"
                    : "block",
              }}
            >
              <Paper
                sx={{
                  height: "100%",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 4,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                {/* Onglets Conversations / Groupes (tous les utilisateurs) */}
                <Tabs
                  value={panelTab}
                  onChange={(_, v) => setPanelTab(v)}
                  variant="fullWidth"
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    minHeight: 44,
                  }}
                >
                  <Tab
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.8 }}
                      >
                         {tr("Conversations")} {conversations.reduce((a, c) => a + c.unreadCount, 0) >
                          0 && (
                          <Box
                            sx={{
                              bgcolor: "error.main",
                              color: "#fff",
                              borderRadius: "50%",
                              minWidth: 18,
                              height: 18,
                              fontSize: "0.65rem",
                              fontWeight: 800,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              px: 0.4,
                            }}
                          >
                            {conversations.reduce(
                              (a, c) => a + c.unreadCount,
                              0
                            )}
                          </Box>
                        )}
                      </Box>
                    }
                    sx={{ fontWeight: 700, fontSize: "0.78rem", minHeight: 44 }}
                  />
                  <Tab
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.8 }}
                      >
                        <Groups sx={{ fontSize: 15 }} />
                         {tr("Groupes")} {totalUnreadGroups > 0 && (
                          <Box
                            sx={{
                              bgcolor: "error.main",
                              color: "#fff",
                              borderRadius: "50%",
                              minWidth: 18,
                              height: 18,
                              fontSize: "0.65rem",
                              fontWeight: 800,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              px: 0.4,
                            }}
                          >
                            {totalUnreadGroups}
                          </Box>
                        )}
                      </Box>
                    }
                    sx={{ fontWeight: 700, fontSize: "0.78rem", minHeight: 44 }}
                  />
                </Tabs>

                {/* Panneau Conversations */}
                {panelTab === 0 && (
                  <>
                    {(effectiveRole === "COACH" ||
                      effectiveRole === "ADMIN") && (
                      <Box
                        sx={{
                          px: 1.5,
                          pt: 1,
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <IconButton
                          aria-label={tr("Ajouter")}
                          size="small"
                          color="primary"
                          onClick={() => {
                            setContactSearch("");
                            setOpenNewConvoModal(true);
                          }}
                          title={tr("Nouvelle conversation")}
                        >
                          <Add />
                        </IconButton>
                      </Box>
                    )}
                    <List sx={{ overflowY: "auto", flexGrow: 1 }}>
                      {conversations.map((convo) => (
                        <React.Fragment key={convo.user.id}>
                          <ListItem
                            disablePadding
                            secondaryAction={
                              <IconButton
                                aria-label={tr("Supprimer")}
                                size="small"
                                edge="end"
                                title={tr("Supprimer")}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConfirmDeleteConvo(convo.user);
                                }}
                                sx={{
                                  color: "error.main",
                                  opacity: 0.6,
                                  "&:hover": { opacity: 1 },
                                  mr: 0.5,
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            }
                          >
                            <ListItemButton
                              onClick={() => {
                                handleSelectConversation(convo);
                                setSelectedGroup(null);
                              }}
                              selected={selectedUser?.id === convo.user.id}
                              sx={{
                                py: 2,
                                pr: 5,
                                borderLeft:
                                  selectedUser?.id === convo.user.id
                                    ? "4px solid"
                                    : "4px solid transparent",
                                borderColor: "primary.main",
                                bgcolor:
                                  selectedUser?.id === convo.user.id
                                    ? "action.selected"
                                    : "inherit",
                              }}
                            >
                              <ListItemAvatar>
                                <Avatar
                                  sx={{
                                    bgcolor: "primary.main",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {convo.user.username.charAt(0).toUpperCase()}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <Typography
                                      fontWeight={
                                        convo.unreadCount > 0 ? 800 : 600
                                      }
                                    >
                                      {convo.user.username}
                                    </Typography>
                                    {convo.unreadCount > 0 && (
                                      <Chip
                                        label={convo.unreadCount}
                                        size="small"
                                        color="error"
                                        sx={{
                                          height: 20,
                                          minWidth: 20,
                                          fontWeight: "bold",
                                        }}
                                      />
                                    )}
                                  </Box>
                                }
                                secondary={
                                  <Typography
                                    variant="body2"
                                    color={
                                      convo.unreadCount > 0
                                        ? "text.primary"
                                        : "text.secondary"
                                    }
                                    noWrap
                                    fontWeight={
                                      convo.unreadCount > 0 ? 600 : 400
                                    }
                                  >
                                    {convo.lastMessage.content}
                                  </Typography>
                                }
                              />
                            </ListItemButton>
                          </ListItem>
                        </React.Fragment>
                      ))}
                      {conversations.length === 0 && (
                        <ListItem>
                          <ListItemText
                            primary="Aucune conversation."
                            secondary={tr("Commencez à discuter !")}
                          />
                        </ListItem>
                      )}
                    </List>
                  </>
                )}

                {/* Panneau Groupes */}
                {panelTab === 1 && (
                  <>
                    <Box
                      sx={{
                        px: 1.5,
                        pt: 1,
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <IconButton
                        aria-label={tr("Ajouter")}
                        size="small"
                        color="primary"
                        title={tr("Créer un groupe")}
                        onClick={() => {
                          setNewGroupName("");
                          setNewGroupMemberIds([]);
                          setOpenCreateGroupModal(true);
                        }}
                      >
                        <Add />
                      </IconButton>
                    </Box>
                    <List sx={{ overflowY: "auto", flexGrow: 1 }}>
                      {groups.map((group) => {
                        const unread = unreadGroupMap[group.id] || 0;
                        const isSelected = selectedGroup?.id === group.id;
                        return (
                          <React.Fragment key={group.id}>
                            <ListItem
                              disablePadding
                              secondaryAction={
                                group.creator === user?.id ? (
                                  <IconButton
                                    aria-label={tr("Supprimer")}
                                    size="small"
                                    edge="end"
                                    title={tr("Supprimer le groupe")}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteGroup(group.id);
                                    }}
                                    sx={{
                                      color: "error.main",
                                      opacity: 0.6,
                                      "&:hover": { opacity: 1 },
                                      mr: 0.5,
                                    }}
                                  >
                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                ) : null
                              }
                            >
                              <ListItemButton
                                onClick={() => handleSelectGroup(group)}
                                selected={isSelected}
                                sx={{
                                  py: 1.5,
                                  pr: 5,
                                  borderLeft: isSelected
                                    ? "4px solid"
                                    : "4px solid transparent",
                                  borderColor: "secondary.main",
                                  bgcolor: isSelected
                                    ? "action.selected"
                                    : "inherit",
                                }}
                              >
                                <ListItemAvatar>
                                  <Avatar
                                    sx={{
                                      bgcolor: isSelected
                                        ? "secondary.dark"
                                        : "secondary.main",
                                      boxShadow:
                                        unread > 0
                                          ? "0 0 0 2px #f44336"
                                          : "none",
                                    }}
                                  >
                                    <Groups sx={{ fontSize: 18 }} />
                                  </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                      }}
                                    >
                                      <Typography
                                        fontWeight={unread > 0 ? 800 : 700}
                                        fontSize="0.875rem"
                                        noWrap
                                        sx={{ maxWidth: 110 }}
                                      >
                                        {group.name}
                                      </Typography>
                                      {unread > 0 && (
                                        <Box
                                          sx={{
                                            bgcolor: "error.main",
                                            color: "#fff",
                                            borderRadius: "50%",
                                            minWidth: 20,
                                            height: 20,
                                            fontSize: "0.68rem",
                                            fontWeight: 800,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            px: 0.5,
                                            flexShrink: 0,
                                          }}
                                        >
                                          {unread}
                                        </Box>
                                      )}
                                    </Box>
                                  }
                                  secondary={
                                    <Typography
                                      variant="caption"
                                      color={
                                        unread > 0
                                          ? "text.primary"
                                          : "text.secondary"
                                      }
                                      noWrap
                                      fontWeight={unread > 0 ? 600 : 400}
                                    >
                                      {group.last_message
                                        ? `${
                                            group.last_message.sender !==
                                            user?.username
                                              ? group.last_message.sender +
                                                " : "
                                              : ""
                                          }${group.last_message.content}`
                                        : `${
                                            group.members_details?.length || 0
                                          } membre${
                                            (group.members_details?.length ||
                                              0) > 1
                                              ? "s"
                                              : ""
                                          }`}
                                    </Typography>
                                  }
                                />
                              </ListItemButton>
                            </ListItem>
                          </React.Fragment>
                        );
                      })}
                      {groups.length === 0 && (
                        <Box sx={{ textAlign: "center", py: 4, opacity: 0.6 }}>
                          <Groups
                            sx={{
                              fontSize: 36,
                              color: "text.secondary",
                              mb: 1,
                            }}
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={600}
                          >
                             {tr("Aucun groupe.")} </Typography>
                          <Typography variant="caption" color="text.secondary">
                             {tr("Créez votre premier groupe !")} </Typography>
                        </Box>
                      )}
                    </List>
                  </>
                )}
              </Paper>
            </Grid>

            {/* Chat Area */}
            <Grid
              size={{ xs: 12, md: 8, lg: 9 }}
              sx={{
                height: "100%",
                display:
                  isMobile && !selectedUser && !selectedGroup
                    ? "none"
                    : "block",
              }}
            >
              <Paper
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 4,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                {selectedUser ? (
                  <React.Fragment>
                    <Box
                      sx={{
                        p: 2,
                        borderBottom: 1,
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        bgcolor: "background.paper",
                      }}
                    >
                      {isMobile && (
                        <IconButton
                          aria-label={tr("Retour")}
                          edge="start"
                          color="inherit"
                          onClick={() => setSelectedUser(null)}
                        >
                          <ArrowBack />
                        </IconButton>
                      )}
                      <Avatar sx={{ bgcolor: "primary.main" }}>
                        {selectedUser.username.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                          {selectedUser.username}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Circle
                            sx={{ fontSize: 10, color: "success.main" }}
                          />
                          <Typography variant="caption" color="text.secondary">
                             {tr("En ligne")} </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        flexGrow: 1,
                        p: 3,
                        overflowY: "auto",
                        display: "flex",
                        flexDirection: "column",
                        bgcolor: "action.hover",
                      }}
                    >
                      {messages.map((msg, index) => {
                        const isMe = Number(msg.sender) === Number(user?.id);
                        const showDate =
                          index === 0 ||
                          new Date(msg.timestamp).toDateString() !==
                            new Date(
                              messages[index - 1].timestamp
                            ).toDateString();

                        return (
                          <React.Fragment key={msg.id}>
                            {showDate && (
                              <Divider
                                sx={{
                                  my: 2,
                                  "&::before": { borderColor: "divider" },
                                  "&::after": { borderColor: "divider" },
                                }}
                              >
                                <Chip
                                  label={format(new Date(msg.timestamp), "PP", {
                                    locale: dateLocale,
                                  })}
                                  size="small"
                                />
                              </Divider>
                            )}
                            <Box
                              onMouseEnter={() => setHoveredMsgId(msg.id)}
                              onMouseLeave={() => setHoveredMsgId(null)}
                              sx={{
                                alignSelf: isMe ? "flex-end" : "flex-start",
                                maxWidth: isMobile ? "85%" : "70%",
                                mb: 2,
                                display: "flex",
                                flexDirection: isMe ? "row-reverse" : "row",
                                alignItems: "flex-end",
                                gap: 1,
                              }}
                            >
                              {!isMe && (
                                <Avatar
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    fontSize: "0.75rem",
                                    bgcolor: "secondary.main",
                                    flexShrink: 0,
                                    mb: 0.5,
                                  }}
                                >
                                  {msg.sender_details?.username
                                    ?.charAt(0)
                                    .toUpperCase() ?? "?"}
                                </Avatar>
                              )}
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: isMe ? "flex-end" : "flex-start",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    flexDirection: isMe ? "row-reverse" : "row",
                                  }}
                                >
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      bgcolor: isMe
                                        ? "primary.main"
                                        : "background.paper",
                                      color: isMe ? "primary.contrastText" : "text.primary",
                                      p: "10px 14px",
                                      borderRadius: isMe
                                        ? "18px 18px 4px 18px"
                                        : "18px 18px 18px 4px",
                                      border: isMe ? "none" : "1px solid",
                                      borderColor: "divider",
                                      boxShadow: isMe
                                        ? "0 4px 12px rgba(0,180,200,0.25)"
                                        : "0 1px 4px rgba(0,0,0,0.08)",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        wordBreak: "break-word", whiteSpace: "pre-wrap",
                                        lineHeight: 1.5,
                                      }}
                                    >
                                      {msg.content}
                                    </Typography>
                                  </Paper>
                                  {isMe && hoveredMsgId === msg.id && (
                                    <IconButton
                                      aria-label={tr("Supprimer")}
                                      size="small"
                                      onClick={() =>
                                        handleDeleteMessage(msg.id)
                                      }
                                      title="Supprimer ce message"
                                      sx={{
                                        color: "error.main",
                                        opacity: 0.7,
                                        "&:hover": { opacity: 1 },
                                        p: 0.5,
                                      }}
                                    >
                                      <DeleteOutline sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  )}
                                </Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    mt: 0.5,
                                    px: 0.5,
                                    color: "text.disabled",
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  {format(new Date(msg.timestamp), "HH:mm")}
                                </Typography>
                              </Box>
                            </Box>
                          </React.Fragment>
                        );
                      })}
                      <div ref={bottomRef} />
                    </Box>

                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "background.paper",
                        borderTop: 1,
                        borderColor: "divider",
                      }}
                    >
                      <MessageComposer
                        key={"user-" + selectedUser.id}
                        onSend={handleSendMessage}
                      />
                    </Box>
                  </React.Fragment>
                ) : selectedGroup ? (
                  /* ── Chat Groupe ── */
                  <React.Fragment>
                    <Box
                      sx={{
                        p: 2,
                        borderBottom: 1,
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        bgcolor: "background.paper",
                      }}
                    >
                      {isMobile && (
                        <IconButton
                          aria-label={tr("Retour")}
                          edge="start"
                          color="inherit"
                          onClick={() => setSelectedGroup(null)}
                        >
                          <ArrowBack />
                        </IconButton>
                      )}
                      <Avatar sx={{ bgcolor: "secondary.main" }}>
                        <Groups />
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                          {selectedGroup.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {selectedGroup.members_details
                            ?.map((m) => m.username)
                            .join(", ")}
                        </Typography>
                      </Box>
                      {selectedGroup.creator === user?.id && (
                        <IconButton
                          aria-label={tr("Ajouter un membre")}
                          size="small"
                          title={tr("Gérer les membres")}
                          onClick={() => setOpenManageGroupModal(true)}
                        >
                          <PersonAdd sx={{ fontSize: 20 }} />
                        </IconButton>
                      )}
                    </Box>

                    <Box
                      sx={{
                        flexGrow: 1,
                        p: 3,
                        overflowY: "auto",
                        display: "flex",
                        flexDirection: "column",
                        bgcolor: "action.hover",
                      }}
                    >
                      {groupMessages.map((msg, index) => {
                        const isMe = Number(msg.sender) === Number(user?.id);
                        const showDate =
                          index === 0 ||
                          new Date(msg.timestamp).toDateString() !==
                            new Date(
                              groupMessages[index - 1].timestamp
                            ).toDateString();
                        return (
                          <React.Fragment key={msg.id}>
                            {showDate && (
                              <Divider sx={{ my: 2 }}>
                                <Chip
                                  label={format(new Date(msg.timestamp), "PP", {
                                    locale: dateLocale,
                                  })}
                                  size="small"
                                />
                              </Divider>
                            )}
                            <Box
                              sx={{
                                alignSelf: isMe ? "flex-end" : "flex-start",
                                maxWidth: isMobile ? "85%" : "70%",
                                mb: 2,
                                display: "flex",
                                flexDirection: isMe ? "row-reverse" : "row",
                                alignItems: "flex-end",
                                gap: 1,
                              }}
                            >
                              {!isMe && (
                                <Avatar
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    fontSize: "0.75rem",
                                    bgcolor: "secondary.main",
                                    flexShrink: 0,
                                    mb: 0.5,
                                  }}
                                >
                                  {msg.sender_details?.username
                                    ?.charAt(0)
                                    .toUpperCase() ?? "?"}
                                </Avatar>
                              )}
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: isMe ? "flex-end" : "flex-start",
                                }}
                              >
                                {!isMe && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      mb: 0.3,
                                      px: 0.5,
                                      fontWeight: 700,
                                      color: "secondary.main",
                                    }}
                                  >
                                    {msg.sender_details?.username}
                                  </Typography>
                                )}
                                <Paper
                                  elevation={0}
                                  sx={{
                                    bgcolor: isMe
                                      ? "secondary.main"
                                      : "background.paper",
                                    color: isMe ? "secondary.contrastText" : "text.primary",
                                    p: "10px 14px",
                                    borderRadius: isMe
                                      ? "18px 18px 4px 18px"
                                      : "18px 18px 18px 4px",
                                    border: isMe ? "none" : "1px solid",
                                    borderColor: "divider",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      wordBreak: "break-word", whiteSpace: "pre-wrap",
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    {msg.content}
                                  </Typography>
                                </Paper>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    mt: 0.5,
                                    px: 0.5,
                                    color: "text.disabled",
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  {format(new Date(msg.timestamp), "HH:mm")}
                                </Typography>
                              </Box>
                            </Box>
                          </React.Fragment>
                        );
                      })}
                      {groupMessages.length === 0 && (
                        <Box sx={{ textAlign: "center", mt: 6, opacity: 0.5 }}>
                          <Groups
                            sx={{
                              fontSize: 48,
                              color: "text.secondary",
                              mb: 1,
                            }}
                          />
                          <Typography color="text.secondary">
                             {tr("Aucun message dans ce groupe.")} </Typography>
                        </Box>
                      )}
                      <div ref={groupBottomRef} />
                    </Box>

                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "background.paper",
                        borderTop: 1,
                        borderColor: "divider",
                      }}
                    >
                      <MessageComposer
                        key={"group-" + selectedGroup.id}
                        onSend={handleSendGroupMessage}
                        placeholder={tr("Message du groupe…")}
                      />
                    </Box>
                  </React.Fragment>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      opacity: 0.5,
                    }}
                  >
                    <Email
                      sx={{ fontSize: 60, mb: 2, color: "text.secondary" }}
                    />
                    <Typography variant="h6" color="text.secondary">
                       {tr("Sélectionnez une conversation")} </Typography>
                    <Typography variant="body2" color="text.secondary">
                       {tr("pour commencer à discuter")} </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Dialog confirmation suppression conversation */}
          <Dialog
            open={!!confirmDeleteConvo}
            onClose={() => setConfirmDeleteConvo(null)}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}
          >
            <DialogTitle
              sx={{
                fontWeight: 800,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <DeleteIcon color="error" />  {tr("Supprimer la conversation")} </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary">
                 {tr("Supprimer tous les messages avec")}{" "}
                <strong>{confirmDeleteConvo?.username}</strong>  {tr("? Cette action est irréversible.")} </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={() => setConfirmDeleteConvo(null)}
                color="inherit"
                sx={{ borderRadius: 2 }}
              >
                 {tr("Annuler")} </Button>
              <Button
                onClick={() =>
                  confirmDeleteConvo &&
                  handleDeleteConversation(confirmDeleteConvo)
                }
                variant="contained"
                color="error"
                sx={{ borderRadius: 2 }}
              >
                 {tr("Supprimer")} </Button>
            </DialogActions>
          </Dialog>

          {/* Invite Modal */}
          <Dialog
            open={openInviteModal}
            onClose={() => setOpenInviteModal(false)}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
          >
            <DialogTitle
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                fontWeight: 800,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
               {tr("Inviter quelqu'un sur G1Sport")} {isMobile && (
                <IconButton
                  aria-label={tr("Fermer")}
                  edge="end"
                  color="inherit"
                  onClick={() => setOpenInviteModal(false)}
                >
                  <Close />
                </IconButton>
              )}
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                 {tr("Invitez un coach ou un sportif à rejoindre G1Sport. Un email leur sera envoyé avec un lien d'inscription.")} </Typography>

              {inviteStatus && (
                <Alert severity={inviteStatus.type} sx={{ mb: 3 }}>
                  {tr(inviteStatus.text)}
                </Alert>
              )}

              <Stack spacing={3} sx={{ mt: 1 }}>
                <TextField
                  label={tr("Nom (Optionnel)")}
                  fullWidth
                  variant="outlined"
                  value={inviteData.name}
                  onChange={(e) =>
                    setInviteData({ ...inviteData, name: e.target.value })
                  }
                  InputProps={{ sx: { borderRadius: 2 } }}
                />
                <TextField
                  label="Adresse Email"
                  fullWidth
                  required
                  type="email"
                  variant="outlined"
                  value={inviteData.email}
                  onChange={(e) =>
                    setInviteData({ ...inviteData, email: e.target.value })
                  }
                  InputProps={{ sx: { borderRadius: 2 } }}
                />
                <TextField
                  label={tr("Message personnalisé (Optionnel)")}
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  value={inviteData.custom_message}
                  onChange={(e) =>
                    setInviteData({
                      ...inviteData,
                      custom_message: e.target.value,
                    })
                  }
                  placeholder={tr("Ajoutez un petit mot personnel...")}
                  InputProps={{ sx: { borderRadius: 2 } }}
                />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0 }}>
              {!isMobile && (
                <Button
                  onClick={() => setOpenInviteModal(false)}
                  color="inherit"
                  sx={{ borderRadius: 2 }}
                >
                   {tr("Annuler")} </Button>
              )}
              <Button
                onClick={handleSendInvite}
                variant="contained"
                disabled={!inviteData.email}
                sx={{ borderRadius: 2, px: 3 }}
                startIcon={<Email />}
                fullWidth={isMobile}
              >
                 {tr("Envoyer l'invitation")} </Button>
            </DialogActions>
          </Dialog>

          {/* Modal Nouvelle Conversation */}
          <Dialog
            open={openNewConvoModal}
            onClose={() => setOpenNewConvoModal(false)}
            maxWidth="xs"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
          >
            <DialogTitle
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                fontWeight: 800,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
               {tr("Nouvelle conversation")} <IconButton
                aria-label={tr("Fermer")}
                size="small"
                onClick={() => setOpenNewConvoModal(false)}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 2, px: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Rechercher..."
                value={contactSearch}
                onChange={(e) => setContactSearch(e.target.value)}
                sx={{ mb: 1.5, mt: 1 }}
              />
              {([tr("Mes Sportifs"), "Coachs"] as const)
                .filter((group) =>
                  availableContacts.some((c: any) => c._group === group)
                )
                .map((group) => {
                  const filtered = availableContacts.filter(
                    (c: any) =>
                      c._group === group &&
                      c.username
                        .toLowerCase()
                        .includes(contactSearch.toLowerCase())
                  );
                  if (filtered.length === 0) return null;
                  return (
                    <Box key={group} sx={{ mb: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: "text.secondary",
                          px: 1,
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                        }}
                      >
                        {group}
                      </Typography>
                      <List disablePadding>
                        {filtered.map((contact: any) => (
                          <ListItem key={contact.id} disablePadding>
                            <ListItemButton
                              onClick={() => {
                                setSelectedUser(contact);
                                setOpenNewConvoModal(false);
                              }}
                              sx={{ borderRadius: 2, py: 1 }}
                            >
                              <ListItemAvatar>
                                <Avatar
                                  sx={{
                                    bgcolor: "primary.main",
                                    width: 34,
                                    height: 34,
                                    fontSize: "0.85rem",
                                    fontWeight: 700,
                                  }}
                                >
                                  {contact.username.charAt(0).toUpperCase()}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={contact.username}
                                secondary={contact.email}
                                primaryTypographyProps={{
                                  fontWeight: 600,
                                  fontSize: "0.875rem",
                                }}
                                secondaryTypographyProps={{
                                  fontSize: "0.75rem",
                                  noWrap: true,
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  );
                })}
              {availableContacts.filter((c: any) =>
                c.username.toLowerCase().includes(contactSearch.toLowerCase())
              ).length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center", py: 3 }}
                >
                   {tr("Aucun contact trouvé.")} </Typography>
              )}
            </DialogContent>
          </Dialog>

          {/* Modal Création de Groupe */}
          <Dialog
            open={openCreateGroupModal}
            onClose={() => setOpenCreateGroupModal(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}
          >
            <DialogTitle
              sx={{
                fontWeight: 800,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Groups color="secondary" />  {tr("Créer un groupe")} </Box>
              <IconButton
                aria-label={tr("Fermer")}
                size="small"
                onClick={() => setOpenCreateGroupModal(false)}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label={tr("Nom du groupe")}
                size="small"
                sx={{ mb: 2, mt: 1 }}
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder={tr("Ex: Équipe cardio, Groupe yoga...")}
                inputProps={{ maxLength: 100 }}
              />
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                 {tr("Membres (")}{newGroupMemberIds.length + 1}/10){" "}
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                >
                   {tr("— vous êtes ajouté automatiquement")} </Typography>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder={tr("Rechercher un sportif...")}
                sx={{ mb: 1.5 }}
                value={groupMemberSearch}
                onChange={(e) => setGroupMemberSearch(e.target.value)}
              />
              <List
                dense
                sx={{
                  maxHeight: 240,
                  overflow: "auto",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                {availableContacts
                  .filter((c) =>
                    c.username
                      .toLowerCase()
                      .includes(groupMemberSearch.toLowerCase())
                  )
                  .map((contact) => {
                    const selected = newGroupMemberIds.includes(contact.id);
                    const atMax = newGroupMemberIds.length >= 9 && !selected;
                    return (
                      <ListItem key={contact.id} disablePadding>
                        <ListItemButton
                          onClick={() => {
                            if (atMax) return;
                            setNewGroupMemberIds((prev) =>
                              selected
                                ? prev.filter((id) => id !== contact.id)
                                : [...prev, contact.id]
                            );
                          }}
                          disabled={atMax}
                          sx={{ py: 0.8 }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                fontSize: "0.8rem",
                                bgcolor: selected
                                  ? "secondary.main"
                                  : "action.disabledBackground",
                              }}
                            >
                              {contact.username.charAt(0).toUpperCase()}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={contact.username}
                            secondary={contact.email}
                            primaryTypographyProps={{
                              fontWeight: selected ? 700 : 400,
                              fontSize: "0.875rem",
                            }}
                          />
                          {selected && (
                            <Chip
                              label="✓"
                              size="small"
                              color="secondary"
                              sx={{ fontWeight: 700 }}
                            />
                          )}
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                {availableContacts.length === 0 && (
                  <ListItem>
                    <ListItemText
                      primary={tr("Aucun contact disponible.")}
                      secondary={tr("Invitez d'abord des sportifs.")}
                    />
                  </ListItem>
                )}
              </List>
              {newGroupMemberIds.length >= 9 && (
                <Alert severity="warning" sx={{ mt: 1.5, fontSize: "0.78rem" }}>
                   {tr("Maximum 10 membres atteint (vous + 9 membres).")} </Alert>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={() => setOpenCreateGroupModal(false)}
                color="inherit"
              >
                 {tr("Annuler")} </Button>
              <Button
                variant="contained"
                color="secondary"
                disabled={!newGroupName.trim()}
                onClick={handleCreateGroup}
                startIcon={<Groups />}
              >
                 {tr("Créer le groupe")} </Button>
            </DialogActions>
          </Dialog>

          {/* Modal Gestion des membres du groupe */}
          {selectedGroup && (
            <Dialog
              open={openManageGroupModal}
              onClose={() => setOpenManageGroupModal(false)}
              maxWidth="sm"
              fullWidth
              PaperProps={{ sx: { borderRadius: 3 } }}
            >
              <DialogTitle
                sx={{
                  fontWeight: 800,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Groups color="secondary" />  {tr("Gérer «")} {selectedGroup.name} »
                </Box>
                <IconButton
                  aria-label={tr("Fermer")}
                  size="small"
                  onClick={() => setOpenManageGroupModal(false)}
                >
                  <Close />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ pt: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                   {tr("Membres actuels (")}{selectedGroup.members_details?.length || 0}
                  /10)
                </Typography>
                <List
                  dense
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    mb: 2,
                  }}
                >
                  {selectedGroup.members_details?.map((member) => (
                    <ListItem
                      key={member.id}
                      secondaryAction={
                        member.id !== user?.id &&
                        member.id !== selectedGroup.creator ? (
                          <IconButton
                            aria-label={tr("Retirer le membre")}
                            size="small"
                            color="error"
                            title="Retirer"
                            onClick={() =>
                              handleRemoveGroupMember(
                                selectedGroup.id,
                                member.id
                              )
                            }
                          >
                            <PersonRemove sx={{ fontSize: 18 }} />
                          </IconButton>
                        ) : null
                      }
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            fontSize: "0.8rem",
                            bgcolor:
                              member.id === selectedGroup.creator
                                ? "warning.main"
                                : "primary.main",
                          }}
                        >
                          {member.username.charAt(0).toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <>
                            {member.username}{" "}
                            {member.id === selectedGroup.creator && (
                              <Chip
                                label={tr("Créateur")}
                                size="small"
                                color="warning"
                                sx={{
                                  ml: 0.5,
                                  height: 18,
                                  fontSize: "0.65rem",
                                }}
                              />
                            )}
                          </>
                        }
                        secondary={member.role}
                      />
                    </ListItem>
                  ))}
                </List>

                {(selectedGroup.members_details?.length || 0) < 10 && (
                  <>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{ mb: 1 }}
                    >
                       {tr("Ajouter un membre")} </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Rechercher..."
                      sx={{ mb: 1 }}
                      value={groupMemberSearch}
                      onChange={(e) => setGroupMemberSearch(e.target.value)}
                    />
                    <List
                      dense
                      sx={{
                        maxHeight: 200,
                        overflow: "auto",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                      }}
                    >
                      {availableContacts
                        .filter(
                          (c) =>
                            !selectedGroup.members?.includes(c.id) &&
                            c.username
                              .toLowerCase()
                              .includes(groupMemberSearch.toLowerCase())
                        )
                        .map((contact) => (
                          <ListItem key={contact.id} disablePadding>
                            <ListItemButton
                              onClick={() => {
                                handleAddGroupMember(
                                  selectedGroup.id,
                                  contact.id
                                );
                              }}
                              sx={{ py: 0.8 }}
                            >
                              <ListItemAvatar>
                                <Avatar
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    fontSize: "0.8rem",
                                    bgcolor: "action.disabledBackground",
                                  }}
                                >
                                  {contact.username.charAt(0).toUpperCase()}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={contact.username}
                                secondary={contact.email}
                                primaryTypographyProps={{
                                  fontSize: "0.875rem",
                                }}
                              />
                              <Add
                                sx={{ fontSize: 18, color: "secondary.main" }}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      {availableContacts.filter(
                        (c) => !selectedGroup.members?.includes(c.id)
                      ).length === 0 && (
                        <ListItem>
                          <ListItemText primary={tr("Tous vos contacts sont déjà membres.")} />
                        </ListItem>
                      )}
                    </List>
                  </>
                )}
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                  onClick={() => setOpenManageGroupModal(false)}
                  variant="contained"
                  color="secondary"
                >
                   {tr("Fermer")} </Button>
              </DialogActions>
            </Dialog>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default Messaging;
